import axios, { AxiosError } from "axios";
import useSWR from "swr";
import { useContext, useMemo } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { MMTradesEndpointEnum } from "../../../enums/endpoints";
import { MMTRADES_API_URL } from "../../../utils/env";
import { IPerpDataSummary } from "../../../interfaces/perpData";

interface IPerpDataSummaryResponse {
  success: boolean;
  message: string;
  data: IPerpDataSummary;
}

interface UsePerpDataSummaryParams {
  startMs: number;
  endMs: number;
  assets?: string[];
  enabled?: boolean;
  refreshInterval?: number;
}

export const normalizePerpDataSummary = (
  summary: IPerpDataSummary
): IPerpDataSummary => ({
  ...summary,
  totals: {
    ...summary.totals,
    netFunding: summary.totals.allVenueNetFunding ?? summary.totals.netFunding,
    combined: summary.totals.allVenueCombined ?? summary.totals.combined,
  },
  pairs: summary.pairs.map((pair) => ({
    ...pair,
    netFunding: pair.allVenueNetFunding ?? pair.netFunding,
    combined: pair.allVenueCombined ?? pair.combined,
  })),
});

export const usePerpDataSummary = ({
  startMs,
  endMs,
  assets,
  enabled = true,
  refreshInterval = 60_000,
}: UsePerpDataSummaryParams) => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const endpoint = MMTRADES_API_URL(MMTradesEndpointEnum.PERP_DATA_SUMMARY);

  const swrKey = useMemo(() => {
    if (!tokenId || !enabled || !startMs || !endMs || startMs >= endMs) return undefined;
    const assetKey = assets?.length ? assets.join(",") : "all";
    return [endpoint, tokenId, startMs, endMs, assetKey];
  }, [endpoint, tokenId, startMs, endMs, assets, enabled]);

  return useSWR<IPerpDataSummary, AxiosError>(
    swrKey,
    async () => {
      const params: Record<string, string> = {
        start_ms: String(startMs),
        end_ms: String(endMs),
      };
      if (assets?.length) {
        params.assets = assets.join(",");
      }

      const response = await axios.get<IPerpDataSummaryResponse>(endpoint, {
        ...queryConfig(),
        params,
      });
      return normalizePerpDataSummary(response.data.data);
    },
    { refreshInterval }
  );
};
